import { useEffect, useMemo, useState } from "react";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon, PrinterIcon } from "@heroicons/react/24/solid";

import useShippingBulk from "../../data/useShippingBulk";
import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";

const BULK_PRINT_BATCH_SIZE = 10;

interface BulkPrintShippingLabelsModalProps {
  isOpen: boolean;
  onClose: () => void;
  parts: IPart[];
}

const openPdfBlobInNewTab = (blob: Blob) => {
  const url = window.URL.createObjectURL(blob);
  if (!url) {
    throw new Error("Failed to create PDF URL.");
  }

  const tab = window.open(url, "_blank");
  if (!tab) {
    window.URL.revokeObjectURL(url);
    throw new Error("Popup blocked. Please allow popups to open batch PDFs.");
  }

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export default function BulkPrintShippingLabelsModal({ isOpen, onClose, parts }: BulkPrintShippingLabelsModalProps) {
  const { bulkPrintShippingLabels } = useShippingBulk();
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [batchIndex, setBatchIndex] = useState(0);

  const printableDocumentIds = useMemo(
    () => parts.map((part) => part.shippingLabelDocumentId).filter((id): id is string => Boolean(id)),
    [parts]
  );

  const batches = useMemo(() => {
    return Array.from(
      { length: Math.ceil(printableDocumentIds.length / BULK_PRINT_BATCH_SIZE) },
      (_, i) => printableDocumentIds.slice(i * BULK_PRINT_BATCH_SIZE, (i + 1) * BULK_PRINT_BATCH_SIZE)
    );
  }, [printableDocumentIds]);

  useEffect(() => {
    if (!isOpen) {
      setBatchIndex(0);
      setLoading(false);
    }
  }, [isOpen, printableDocumentIds.length]);

  const totalBatches = Math.max(batches.length, 1);
  const allBatchesOpened = batchIndex >= batches.length && batches.length > 0;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handlePrintCurrentBatch = async () => {
    const currentBatch = batches[batchIndex];
    if (!currentBatch || currentBatch.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: "No labels found to print",
      });
      return;
    }

    setLoading(true);

    try {
      const blob = await bulkPrintShippingLabels.mutateAsync(currentBatch);
      openPdfBlobInNewTab(blob);

      addNotification({
        variant: "info",
        primaryText: `Batch ${batchIndex + 1} opened`,
        secondaryText: "Print manually in that tab (Ctrl+P).",
      });

      const nextIndex = batchIndex + 1;
      if (nextIndex >= batches.length) {
        setBatchIndex(batches.length);
        addNotification({
          variant: "success",
          primaryText: "All batch PDFs opened",
          secondaryText: "Print each tab manually (Ctrl+P).",
        });
      } else {
        setBatchIndex(nextIndex);
      }
    } catch (err: any) {
      addNotification({
        variant: "error",
        primaryText: "Failed to prepare current print batch",
        secondaryText: err?.message ?? "Please try this batch again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} setOpen={handleClose} width="max-w-lg mx-4">
      <div className="relative">
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className="absolute right-0 top-0 rounded-md p-1 text-gray-400 hover:text-gray-600 disabled:opacity-40"
          aria-label="Close"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="flex items-center gap-3 pr-8">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50">
            <PrinterIcon className="h-5 w-5 text-primary-600" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bulk Print Shipping Labels</h2>
            <p className="text-sm text-gray-500">Reprint existing labels for the selected orders.</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Selected</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{parts.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Printable now</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{printableDocumentIds.length}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <StatusPill
            text={`${totalBatches} batch${totalBatches === 1 ? "" : "es"} of ${BULK_PRINT_BATCH_SIZE}`}
            colour={StatusPillColour.Blue}
          />
          <StatusPill
            text={allBatchesOpened ? "All batches opened" : `Batch ${Math.min(batchIndex + 1, totalBatches)} of ${totalBatches}`}
            colour={allBatchesOpened ? StatusPillColour.Green : StatusPillColour.Neutral}
          />
        </div>

        <div className="mt-4 space-y-2 rounded-lg bg-amber-50 p-3 ring-1 ring-inset ring-amber-200">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">
              Each batch opens as a PDF tab - you must print manually in that tab (Ctrl+P).
            </p>
          </div>
          <p className="pl-6 text-xs text-amber-800">
            Only existing labels are printed; items without a label are skipped.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <ButtonInput
            label={allBatchesOpened ? "Close" : "Cancel"}
            onClick={handleClose}
            classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
            isSubmit={false}
            disabled={loading}
          />
          <ButtonInput
            label={allBatchesOpened ? `All ${totalBatches} batches opened` : `Open Batch ${Math.min(batchIndex + 1, totalBatches)} of ${totalBatches}`}
            onClick={handlePrintCurrentBatch}
            classes=""
            isSubmit={false}
            isLoading={loading}
            disabled={loading || batches.length === 0 || batchIndex >= batches.length}
          />
        </div>
      </div>
    </Modal>
  );
}
